import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  getMatchById, 
  getMatchStatistics, 
  getLineups, 
  getLiveEvents, 
  getHighlights 
} from '../services/api';
import { BRAND_GREEN, BRAND_GOLD, BRAND_GREEN_LIGHT } from '../theme/sucronTheme';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    errorTitle: 'Ocorreu um erro',
    backHome: 'Voltar para Início',
    notFound: 'Dados da partida não encontrados',
    idMissing: 'ID da partida não fornecido',
    loadError: 'Erro ao carregar dados da partida',
    liveNow: 'AO VIVO',
    watchLive: 'Assistir ao Vivo',
    close: 'Fechar',
    streamUnavailable: 'Stream indisponível pelo provedor. Tente os',
    streamLink: 'melhores momentos',
    position: 'Posição',
    starter: '(Titular)',
    sub: '(Reserva)',
    tabs: {
      overview: 'Visão Geral',
      stats: 'Estatísticas',
      lineups: 'Escalações',
      players: 'Jogadores',
      events: 'Eventos',
      highlights: 'Destaques',
    },
    matchInfo: 'Informações da Partida',
    competition: 'Competição:',
    date: 'Data:',
    time: 'Horário:',
    round: 'Rodada:',
    stadium: 'Estádio:',
    referee: 'Autórbitro:',
    status: 'Status:',
    statusLive: '⚽ Ao Vivo',
    statusFinished: '✓ Finalizado',
    statusScheduled: '📅 Agendado',
    mainStats: 'Estatísticas Principais',
    possession: 'Posse de Bola',
    shots: 'Finalizações',
    shotsOnTarget: 'Finalizações no Alvo',
    corners: 'Escanteios',
    fouls: 'Faltas',
    tackles: 'Desarmes',
    lineupUnavailable: 'Escalação não disponível',
    homeTeamDefault: 'Time da Casa',
    awayTeamDefault: 'Time Visitante',
    detailedStats: 'Estatísticas Detalhadas',
    statsUnavailable: 'Estatísticas detalhadas não disponíveis para esta partida',
    possession_group: 'POSSE E CONTROLE',
    attack_group: 'ATAQUE',
    defense_group: 'DEFESA E DISCIPLINA',
    passes: 'Passes Totais',
    passesCompleted: 'Passes Completados',
    passAccuracy: 'Precisão de Passes',
    offsides: 'Impedimentos',
    yellowCards: 'Cartões Amarelos',
    redCards: 'Cartões Vermelhos',
    playerStatsUnavailable: 'Estatísticas individuais não disponíveis',
    individualStats: 'Estatísticas Individuais',
    colPlayer: 'JOGADOR', colMin: 'MIN', colGoals: 'GOLS', colAss: 'ASS', colShots: 'CHUTES', colRating: 'NOTA',
    matchTimeline: 'Cronologia da Partida',
    noEvents: 'Nenhum evento registrado ainda',
    highlights: 'Melhores Momentos',
    highlightsUnavailable: 'Destaques não disponíveis',
    dateLocale: 'pt-BR',
  },
  en: {
    errorTitle: 'An error occurred',
    backHome: 'Back to Home',
    notFound: 'Match data not found',
    idMissing: 'Match ID not provided',
    loadError: 'Error loading match data',
    liveNow: 'LIVE',
    watchLive: 'Watch Live',
    close: 'Close',
    streamUnavailable: 'Stream unavailable from provider. Try the',
    streamLink: 'highlights',
    position: 'Position',
    starter: '(Starter)',
    sub: '(Sub)',
    tabs: {
      overview: 'Overview',
      stats: 'Statistics',
      lineups: 'Lineups',
      players: 'Players',
      events: 'Events',
      highlights: 'Highlights',
    },
    matchInfo: 'Match Information',
    competition: 'Competition:',
    date: 'Date:',
    time: 'Time:',
    round: 'Round:',
    stadium: 'Stadium:',
    referee: 'Referee:',
    status: 'Status:',
    statusLive: '⚽ Live',
    statusFinished: '✓ Finished',
    statusScheduled: '📅 Scheduled',
    mainStats: 'Key Statistics',
    possession: 'Possession',
    shots: 'Shots',
    shotsOnTarget: 'Shots on Target',
    corners: 'Corners',
    fouls: 'Fouls',
    tackles: 'Tackles',
    lineupUnavailable: 'Lineup not available',
    homeTeamDefault: 'Home Team',
    awayTeamDefault: 'Away Team',
    detailedStats: 'Detailed Statistics',
    statsUnavailable: 'Detailed statistics not available for this match',
    possession_group: 'POSSESSION & CONTROL',
    attack_group: 'ATTACK',
    defense_group: 'DEFENSE & DISCIPLINE',
    passes: 'Total Passes',
    passesCompleted: 'Completed Passes',
    passAccuracy: 'Pass Accuracy',
    offsides: 'Offsides',
    yellowCards: 'Yellow Cards',
    redCards: 'Red Cards',
    playerStatsUnavailable: 'Individual stats not available',
    individualStats: 'Individual Statistics',
    colPlayer: 'PLAYER', colMin: 'MIN', colGoals: 'GLS', colAss: 'AST', colShots: 'SHT', colRating: 'RTG',
    matchTimeline: 'Match Timeline',
    noEvents: 'No events recorded yet',
    highlights: 'Highlights',
    highlightsUnavailable: 'Highlights not available',
    dateLocale: 'en-US',
  }
};

// --- Cores da marca ---

// --- UI Components ---
const SectionTitle = ({ title, className = "" }) => (
  <h2 className={`text-2xl lg:text-3xl font-extrabold mb-6 text-center lg:text-left ${className}`} style={{ color: BRAND_GREEN }}>
    {title}
  </h2>
);

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 lg:p-5 ${className}`}>
    {children}
  </div>
);

const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-10 w-full">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: BRAND_GREEN }}></div>
  </div>
);

const ErrorMessage = ({ message }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="text-center py-6 text-red-700 bg-red-100 p-4 rounded-lg border border-red-200">
    <p className="font-bold">{t.errorTitle}</p>
    <p className="text-sm">{message}</p>
  </div>
  );
};

// --- Dados Mockados ---
// --- Componente de Jogador ---
const PlayerCard = ({ player, isHome }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  return (
  <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100">
    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm"
         style={{ backgroundColor: isHome ? BRAND_GREEN : BRAND_GOLD, color: 'white' }}>
      {player.number || '?'}
    </div>
    <div className="flex-1">
      <div className="font-semibold text-gray-900">{player.name}</div>
      <div className="text-xs text-gray-600">{player.position || t.position}</div>
      {player.minutes !== undefined && (
        <div className="text-xs text-gray-500 mt-1">
          ⏱️ {player.minutes}' {player.starter ? t.starter : t.sub}
        </div>
      )}
    </div>
    <div className="text-right">
      {player.rating && player.rating > 0 && (
        <div className="text-sm font-bold mb-1" style={{ color: BRAND_GREEN }}>
          ★ {player.rating.toFixed(1)}
        </div>
      )}
      <div className="text-xs text-gray-600 space-y-0.5">
        {player.goals > 0 && <div>⚽ {player.goals}</div>}
        {player.assists > 0 && <div>�️ {player.assists}</div>}
        {player.shots > 0 && <div>🎯 {player.shots}</div>}
        {player.tackles > 0 && <div>🛡️ {player.tackles}</div>}
        {player.keyPasses > 0 && <div>🔑 {player.keyPasses}</div>}
      </div>
    </div>
  </div>
  );
};

// --- Componente de Estatística ---
const StatBar = ({ label, homeValue, awayValue, maxValue }) => {
  // Se maxValue não for fornecido, calcula baseado nos valores reais
  const calculatedMax = maxValue || Math.max(homeValue, awayValue, 1);
  const homePercentage = (homeValue / calculatedMax) * 100;
  const awayPercentage = (awayValue / calculatedMax) * 100;
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm font-semibold">
        <span>{homeValue}</span>
        <span className="text-gray-600">{label}</span>
        <span>{awayValue}</span>
      </div>
      <div className="flex gap-2">
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all duration-500"
            style={{ 
              width: `${homePercentage}%`, 
              backgroundColor: BRAND_GREEN 
            }}
          />
        </div>
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div 
            className="h-2 rounded-full transition-all duration-500"
            style={{ 
              width: `${awayPercentage}%`, 
              backgroundColor: BRAND_GOLD 
            }}
          />
        </div>
      </div>
    </div>
  );
};

// --- Componente de Evento ---
const EventCard = ({ event }) => {
  const getEventIcon = (type) => {
    switch (type) {
      case 'goal': return '⚽';
      case 'yellow': return '🟨';
      case 'red': return '🟥';
      case 'substitution': return '🔄';
      default: return '📝';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'goal': return BRAND_GREEN;
      case 'yellow': return '#fbbf24';
      case 'red': return '#ef4444';
      case 'substitution': return '#6b7280';
      default: return '#374151';
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm"
           style={{ backgroundColor: getEventColor(event.type), color: 'white' }}>
        {event.minute}'
      </div>
      <div className="flex-1">
        <div className="font-semibold text-gray-900">{event.description}</div>
        <div className="text-sm text-gray-600">{event.player}</div>
      </div>
      <div className="text-2xl">{getEventIcon(event.type)}</div>
    </div>
  );
};

// --- Componente de Destaque ---
const HighlightCard = ({ highlight }) => (
  <div className="p-4 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
       style={{ borderLeft: `4px solid ${BRAND_GREEN}` }}>
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold text-white"
           style={{ backgroundColor: BRAND_GREEN }}>
        {highlight.time}
      </div>
      <div className="flex-1">
        <div className="font-semibold text-gray-900">{highlight.title}</div>
        <div className="text-sm text-gray-600">{highlight.description}</div>
      </div>
      <div className="text-2xl">▶️</div>
    </div>
  </div>
);

// --- Modal de Transmissão ---
const LiveStreamModal = ({ open, onClose, match }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  if (!open) return null;
  const url = match?.streamUrl || '';
  const hasUrl = !!url;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: `${BRAND_GREEN}33` }}>
          <div className="flex items-center gap-3">
            <span className="text-xs font-black px-2 py-1 rounded" style={{ backgroundColor: BRAND_GOLD, color: 'white' }}>
              {t.liveNow}
            </span>
            <h4 className="font-bold" style={{ color: BRAND_GREEN }}>
              {match?.homeTeam?.name} vs {match?.awayTeam?.name}
            </h4>
          </div>
          <button onClick={onClose} className="px-3 py-1 text-sm font-semibold text-white rounded-md" style={{ backgroundColor: BRAND_GREEN }}>
            {t.close}
          </button>
        </div>
        <div className="aspect-video w-full bg-black">
          {hasUrl ? (
            <iframe title="live-player" src={url} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <p className="text-white text-center px-6">
                {t.streamUnavailable} <Link to={`/futebol/partida/${match?.id}?tab=highlights`} className="underline">{t.streamLink}</Link>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Página Principal ---
const MatchPage = () => {
  const { matchId } = useParams();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openStream, setOpenStream] = useState(false);
  
  // Estados para dados reais do backend
  const [matchData, setMatchData] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [lineups, setLineups] = useState({ home: [], away: [] });
  const [events, setEvents] = useState([]);
  const [highlights, setHighlights] = useState([]);

  // Carregar dados da partida do backend
  useEffect(() => {
    const loadMatchData = async () => {
      if (!matchId) {
        setError(t.idMissing);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('[MatchPage] Carregando dados da partida:', matchId);

        // Buscar dados do backend próprio (ArenaStats)
        const matchResponse = await getMatchById({ id: matchId });
        console.log('[MatchPage] ✅ Dados da partida recebidos:', matchResponse.data);
        
        // O backend já retorna em camelCase, usar diretamente
        const match = matchResponse.data;
        
        // Se os dados já vierem formatados, usar diretamente
        // Caso contrário, fazer fallback para os campos antigos
        const formattedMatch = {
          id: match.id,
          homeTeam: match.homeTeam || {
            id: match.home_team_id,
            name: match.home_team_name,
            logo: match.home_team_logo,
            score: match.home_team_goals || match.home_score || 0
          },
          awayTeam: match.awayTeam || {
            id: match.away_team_id,
            name: match.away_team_name,
            logo: match.away_team_logo,
            score: match.away_team_goals || match.away_score || 0
          },
          status: match.status || 'Finalizado',
          isLive: match.isLive || match.status?.toLowerCase().includes('live') || match.status?.toLowerCase().includes('andamento'),
          isFinished: match.isFinished || match.status?.toLowerCase().includes('finished') || match.status?.toLowerCase().includes('finalizado'),
          competition: match.competition || match.league_name || 'Campeonato',
          date: match.date || match.match_date || new Date().toISOString(),
          round: match.round,
          venue: match.venue || 'Estádio',
          referee: match.referee || 'A definir',
          streamUrl: match.streamUrl || match.stream_url || null
        };
        
        console.log('[MatchPage] 🎯 Partida formatada:', formattedMatch);
        setMatchData(formattedMatch);

        // Buscar dados adicionais em paralelo
        const [statsResponse, lineupsResponse, eventsResponse, highlightsResponse] = await Promise.allSettled([
          getMatchStatistics({ matchId }),
          getLineups({ matchId }),
          getLiveEvents({ matchId }),
          getHighlights({ matchId })
        ]);

        // Processar estatísticas
        if (statsResponse.status === 'fulfilled' && statsResponse.value?.data) {
          console.log('[MatchPage] ✅ Estatísticas recebidas:', statsResponse.value.data);
          setStatistics(statsResponse.value.data);
        } else {
          console.warn('[MatchPage] ⚠️ Estatísticas não disponíveis');
        }

        // Processar escalações
        if (lineupsResponse.status === 'fulfilled' && lineupsResponse.value?.data) {
          console.log('[MatchPage] ✅ Escalações recebidas:', lineupsResponse.value.data);
          setLineups(lineupsResponse.value.data);
        } else {
          console.warn('[MatchPage] ⚠️ Escalações não disponíveis');
        }

        // Processar eventos
        if (eventsResponse.status === 'fulfilled' && eventsResponse.value?.data) {
          console.log('[MatchPage] ✅ Eventos recebidos:', eventsResponse.value.data);
          const eventsData = eventsResponse.value.data;
          const eventsArray = Array.isArray(eventsData) ? eventsData : (eventsData.events || []);
          setEvents(eventsArray);
        } else {
          console.warn('[MatchPage] ⚠️ Eventos não disponíveis');
        }

        // Processar destaques
        if (highlightsResponse.status === 'fulfilled' && highlightsResponse.value?.data) {
          console.log('[MatchPage] ✅ Destaques recebidos:', highlightsResponse.value.data);
          const highlightsData = highlightsResponse.value.data;
          const highlightsArray = Array.isArray(highlightsData) ? highlightsData : (highlightsData.highlights || []);
          setHighlights(highlightsArray);
        } else {
          console.warn('[MatchPage] ⚠️ Destaques não disponíveis');
        }

      } catch (error) {
        console.error('[MatchPage] ❌ Erro ao carregar dados:', error);
        setError(t.loadError);
      } finally {
        setLoading(false);
      }
    };

    loadMatchData();
  }, [matchId]);

  // Usar apenas dados reais, sem fallback para mock
  const match = matchData;
  const currentStats = statistics;
  const currentLineups = lineups;
  const currentEvents = events;
  const currentHighlights = highlights;

  const tabs = [
    { id: 'overview', label: t.tabs.overview },
    { id: 'stats', label: t.tabs.stats },
    { id: 'lineups', label: t.tabs.lineups },
    { id: 'players', label: t.tabs.players },
    { id: 'events', label: t.tabs.events },
    { id: 'highlights', label: t.tabs.highlights }
  ];

  const openLiveStream = useCallback(() => {
    setOpenStream(true);
  }, []);

  const closeLiveStream = useCallback(() => {
    setOpenStream(false);
  }, []);

  // Loading state
  if (loading) {
    return (
      <main className="container mx-auto px-4 lg:px-8 py-10" style={{ backgroundColor: '#f9fafb' }}>
        <LoadingSpinner />
      </main>
    );
  }

  // Error state
  if (error) {
    return (
      <main className="container mx-auto px-4 lg:px-8 py-10" style={{ backgroundColor: '#f9fafb' }}>
        <ErrorMessage message={error} />
        <div className="text-center mt-4">
          <Link
            to="/"
            className="inline-block h-11 px-4 flex items-center justify-center text-sm font-semibold text-white rounded-xl hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ backgroundColor: BRAND_GREEN, '--tw-ring-color': BRAND_GREEN }}
          >
            {t.backHome}
          </Link>
        </div>
      </main>
    );
  }

  // Verificar se temos dados da partida
  if (!match) {
    return (
      <main className="container mx-auto px-4 lg:px-8 py-10" style={{ backgroundColor: '#f9fafb' }}>
        <ErrorMessage message={t.notFound} />
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 lg:px-8 py-10" style={{ backgroundColor: '#f9fafb' }}>
      {/* Header da Partida */}
      <div className="mb-8">
        <Card>
          <div className="text-center">
            <div className="text-sm text-gray-600 mb-2">{match.competition}</div>
            <div className="text-lg text-gray-600 mb-4">{match.venue} • {match.referee}</div>
            
            {/* Times e Placar */}
            <div className="flex items-center justify-center gap-8 mb-6">
              <div className="text-center">
                <img src={match.homeTeam.logo} alt={match.homeTeam.name} className="w-16 h-16 mx-auto mb-2" />
                <div className="font-bold text-lg" style={{ color: BRAND_GREEN }}>{match.homeTeam.name}</div>
              </div>
              
              <div className="text-center">
                {match.isLive ? (
                  <div className="text-xs font-black px-3 py-1 rounded-full text-white mb-2" style={{ backgroundColor: BRAND_GOLD }}>
                    {t.liveNow}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 mb-2">
                    {new Date(match.date).toLocaleDateString(t.dateLocale)}
                  </div>
                )}
                <div className="text-4xl font-bold" style={{ color: BRAND_GREEN }}>
                  {match.homeTeam.score} - {match.awayTeam.score}
                </div>
                <div className="text-sm text-gray-600 mt-1">{match.status}</div>
              </div>
              
              <div className="text-center">
                <img src={match.awayTeam.logo} alt={match.awayTeam.name} className="w-16 h-16 mx-auto mb-2" />
                <div className="font-bold text-lg" style={{ color: BRAND_GREEN }}>{match.awayTeam.name}</div>
              </div>
            </div>

            {/* Botão Assistir ao Vivo */}
            {match.isLive && (
              <button
                onClick={openLiveStream}
                className="px-6 py-3 text-sm font-bold text-white rounded-full shadow-sm hover:shadow-md transition-all duration-200"
                style={{ backgroundColor: BRAND_GOLD }}
              >
                {t.watchLive}
              </button>
            )}
          </div>
        </Card>
      </div>

      {/* Tabs de Navegação */}
      <div className="mb-8">
        <Card>
          <div className="flex flex-wrap gap-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'text-white shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
                style={{ 
                  backgroundColor: activeTab === tab.id ? BRAND_GREEN : 'transparent'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Conteúdo das Tabs */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="font-bold text-lg mb-4" style={{ color: BRAND_GREEN }}>{t.matchInfo}</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">{t.competition}</span>
                <span className="font-semibold">{match.competition}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t.date}</span>
                <span className="font-semibold">
                  {new Date(match.date).toLocaleDateString(t.dateLocale, { 
                    day: '2-digit', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t.time}</span>
                <span className="font-semibold">
                  {new Date(match.date).toLocaleTimeString(t.dateLocale, { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
              {match.round && (
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.round}</span>
                  <span className="font-semibold">{match.round}ª</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">{t.stadium}</span>
                <span className="font-semibold">{match.venue}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t.referee}</span>
                <span className="font-semibold">{match.referee}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t.status}</span>
                <span className={`font-semibold ${match.isFinished ? 'text-gray-900' : match.isLive ? 'text-green-600' : 'text-blue-600'}`}>
                  {match.isLive ? t.statusLive : match.isFinished ? t.statusFinished : t.statusScheduled}
                </span>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="font-extrabold text-lg mb-4" style={{ color: BRAND_GREEN }}>{t.mainStats}</h3>
            <div className="space-y-4">
              <StatBar label={t.possession} homeValue={currentStats.possession.home} awayValue={currentStats.possession.away} maxValue={100} />
              <StatBar label={t.shots} homeValue={currentStats.shots.home} awayValue={currentStats.shots.away} />
              <StatBar label={t.shotsOnTarget} homeValue={currentStats.shotsOnTarget.home} awayValue={currentStats.shotsOnTarget.away} />
              <StatBar label={t.corners} homeValue={currentStats.corners.home} awayValue={currentStats.corners.away} />
              <StatBar label={t.fouls} homeValue={currentStats.fouls.home} awayValue={currentStats.fouls.away} />
              <StatBar label={t.tackles} homeValue={currentStats.tackles?.home || 0} awayValue={currentStats.tackles?.away || 0} />
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'lineups' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
                <h3 className="font-extrabold text-lg mb-4" style={{ color: BRAND_GREEN }}>{match.homeTeam?.name || t.homeTeamDefault}</h3>
            <div className="space-y-2">
              {currentLineups.home && currentLineups.home.length > 0 ? (
                currentLineups.home.map((player, index) => (
                  <PlayerCard key={index} player={player} isHome={true} />
                ))
                  ) : (
                <p className="text-gray-600 text-center py-4">{t.lineupUnavailable}</p>
              )}
            </div>
          </Card>

          <Card>
            <h3 className="font-extrabold text-lg mb-4" style={{ color: BRAND_GREEN }}>{match.awayTeam?.name || t.awayTeamDefault}</h3>
            <div className="space-y-2">
              {currentLineups.away && currentLineups.away.length > 0 ? (
                currentLineups.away.map((player, index) => (
                  <PlayerCard key={index} player={player} isHome={false} />
                ))
              ) : (
                <p className="text-gray-600 text-center py-4">{t.lineupUnavailable}</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'stats' && (
        <Card>
          <h3 className="font-extrabold text-lg mb-6" style={{ color: BRAND_GREEN }}>{t.detailedStats}</h3>
          {currentStats.shots.home === 0 && currentStats.shots.away === 0 && currentStats.corners.home === 0 && currentStats.corners.away === 0 ? (
            <p className="text-gray-600 text-center py-8">{t.statsUnavailable}</p>
          ) : (
            <div className="space-y-6">
              {/* Posse e Controle */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t.possession_group}</h4>
                <div className="space-y-4">
                  <StatBar label={t.possession} homeValue={currentStats.possession.home} awayValue={currentStats.possession.away} maxValue={100} />
                  <StatBar label={t.passes} homeValue={currentStats.passes.home} awayValue={currentStats.passes.away} />
                  <StatBar label={t.passesCompleted} homeValue={currentStats.passesCompleted?.home || 0} awayValue={currentStats.passesCompleted?.away || 0} />
                  <StatBar label={t.passAccuracy} homeValue={currentStats.passAccuracy.home} awayValue={currentStats.passAccuracy.away} maxValue={100} />
                </div>
              </div>

              {/* Ataque */}
              <div className="border-b border-gray-200 pb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t.attack_group}</h4>
                <div className="space-y-4">
                  <StatBar label={t.shots} homeValue={currentStats.shots.home} awayValue={currentStats.shots.away} />
                  <StatBar label={t.shotsOnTarget} homeValue={currentStats.shotsOnTarget.home} awayValue={currentStats.shotsOnTarget.away} />
                  <StatBar label={t.corners} homeValue={currentStats.corners.home} awayValue={currentStats.corners.away} />
                </div>
              </div>

              {/* Defesa e Disciplina */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">{t.defense_group}</h4>
                <div className="space-y-4">
                  <StatBar label={t.tackles} homeValue={currentStats.tackles?.home || 0} awayValue={currentStats.tackles?.away || 0} />
                  <StatBar label={t.fouls} homeValue={currentStats.fouls.home} awayValue={currentStats.fouls.away} />
                  <StatBar label={t.offsides} homeValue={currentStats.offsides?.home || 0} awayValue={currentStats.offsides?.away || 0} />
                  <StatBar label={t.yellowCards} homeValue={currentStats.yellowCards?.home || 0} awayValue={currentStats.yellowCards?.away || 0} />
                  <StatBar label={t.redCards} homeValue={currentStats.redCards?.home || 0} awayValue={currentStats.redCards?.away || 0} />
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'players' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <h3 className="font-extrabold text-lg mb-4" style={{ color: BRAND_GREEN }}>
              {match.homeTeam?.name || t.homeTeamDefault} - {t.individualStats}
            </h3>
            {currentLineups.home && currentLineups.home.length > 0 ? (
              <div className="space-y-1">
                <div className="grid grid-cols-7 gap-2 text-xs font-semibold text-gray-600 pb-2 border-b">
                  <div className="col-span-2">{t.colPlayer}</div>
                  <div className="text-center">{t.colMin}</div>
                  <div className="text-center">{t.colGoals}</div>
                  <div className="text-center">{t.colAss}</div>
                  <div className="text-center">{t.colShots}</div>
                  <div className="text-center">{t.colRating}</div>
                </div>
                {currentLineups.home
                  .sort((a, b) => (b.starter ? 1 : 0) - (a.starter ? 1 : 0) || (b.minutes || 0) - (a.minutes || 0))
                  .map((player, index) => (
                    <div key={index} className={`grid grid-cols-7 gap-2 text-sm py-2 border-b border-gray-100 ${player.starter ? 'bg-green-50' : ''}`}>
                      <div className="col-span-2 font-semibold flex items-center gap-1">
                        <span className="text-xs font-bold text-white px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND_GREEN }}>
                          {player.number || '?'}
                        </span>
                        {player.name}
                      </div>
                      <div className="text-center text-gray-700">{player.minutes || 0}'</div>
                      <div className="text-center font-semibold">{player.goals || 0}</div>
                      <div className="text-center font-semibold">{player.assists || 0}</div>
                      <div className="text-center">{player.shots || 0}</div>
                      <div className="text-center font-bold" style={{ color: BRAND_GREEN }}>
                        {player.rating && player.rating > 0 ? player.rating.toFixed(1) : '-'}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center py-4">{t.playerStatsUnavailable}</p>
            )}
          </Card>

          <Card>
            <h3 className="font-extrabold text-lg mb-4" style={{ color: BRAND_GREEN }}>
              {match.awayTeam?.name || t.awayTeamDefault} - {t.individualStats}
            </h3>
            {currentLineups.away && currentLineups.away.length > 0 ? (
              <div className="space-y-1">
                <div className="grid grid-cols-7 gap-2 text-xs font-semibold text-gray-600 pb-2 border-b">
                  <div className="col-span-2">{t.colPlayer}</div>
                  <div className="text-center">{t.colMin}</div>
                  <div className="text-center">{t.colGoals}</div>
                  <div className="text-center">{t.colAss}</div>
                  <div className="text-center">{t.colShots}</div>
                  <div className="text-center">{t.colRating}</div>
                </div>
                {currentLineups.away
                  .sort((a, b) => (b.starter ? 1 : 0) - (a.starter ? 1 : 0) || (b.minutes || 0) - (a.minutes || 0))
                  .map((player, index) => (
                    <div key={index} className={`grid grid-cols-7 gap-2 text-sm py-2 border-b border-gray-100 ${player.starter ? 'bg-yellow-50' : ''}`}>
                      <div className="col-span-2 font-semibold flex items-center gap-1">
                        <span className="text-xs font-bold text-white px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND_GOLD }}>
                          {player.number || '?'}
                        </span>
                        {player.name}
                      </div>
                      <div className="text-center text-gray-700">{player.minutes || 0}'</div>
                      <div className="text-center font-semibold">{player.goals || 0}</div>
                      <div className="text-center font-semibold">{player.assists || 0}</div>
                      <div className="text-center">{player.shots || 0}</div>
                      <div className="text-center font-bold" style={{ color: BRAND_GOLD }}>
                        {player.rating && player.rating > 0 ? player.rating.toFixed(1) : '-'}
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center py-4">{t.playerStatsUnavailable}</p>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'events' && (
        <Card>
          <h3 className="font-extrabold text-lg mb-6" style={{ color: BRAND_GREEN }}>{t.matchTimeline}</h3>
          <div className="space-y-2">
            {currentEvents && currentEvents.length > 0 ? (
              currentEvents.map((event, index) => (
                <EventCard key={index} event={event} />
              ))
            ) : (
              <p className="text-gray-600 text-center py-4">{t.noEvents}</p>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'highlights' && (
        <Card>
          <h3 className="font-extrabold text-lg mb-6" style={{ color: BRAND_GREEN }}>{t.highlights}</h3>
          <div className="space-y-4">
            {currentHighlights && currentHighlights.length > 0 ? (
              currentHighlights.map((highlight, index) => (
                <HighlightCard key={index} highlight={highlight} />
              ))
            ) : (
              <p className="text-gray-600 text-center py-4">{t.highlightsUnavailable}</p>
            )}
          </div>
        </Card>
      )}

      {/* Modal de Transmissão */}
      <LiveStreamModal
        open={openStream}
        onClose={closeLiveStream}
        match={match}
      />
    </main>
  );
};

export default MatchPage;
